import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, BellOff, CheckCircle2, Calendar, Users, Package, AlertTriangle, Zap, ChevronRight, CheckCheck, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const CATEGORY_CONFIG = {
  hr: { icon: Users, color: 'text-success', bg: 'bg-success/10' },
  inventory: { icon: Package, color: 'text-warning', bg: 'bg-warning/10' },
  system: { icon: Zap, color: 'text-info', bg: 'bg-info/10' },
  tier_limit: { icon: AlertTriangle, color: 'text-error', bg: 'bg-error/10' },
};

const PRIORITY_STYLES = {
  high: 'border-l-4 border-l-red-400',
  medium: 'border-l-4 border-l-amber-400',
  low: 'border-l-4 border-l-stone-200',
};

export default function NotificationCenterPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ notifications: [], unread_count: 0, total: 0 });
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [notifRes, tierRes] = await Promise.all([
        api.get('/notifications/center'),
        api.get('/notifications/tier-check'),
      ]);
      setData(notifRes.data);
      setTierData(tierRes.data);
    } catch { toast.error('Failed to load notifications'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const markAllRead = async () => {
    try {
      await api.post('/notifications/center/mark-all-read');
      toast.success('All notifications marked as read');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleClick = (notif) => {
    if (notif.action_url) navigate(notif.action_url);
  };

  const formatTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch { return ''; }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="notification-center-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg shadow-red-500/20">
              <Bell size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="notification-center-title">Notification Center</h1>
              <p className="text-[13px] text-muted-foreground mt-0.5 font-medium">All alerts, approvals & system notifications</p>
            </div>
          </div>
          {data.unread_count > 0 && (
            <Button variant="outline" size="sm" onClick={markAllRead} className="rounded-xl h-9 text-[13px]" data-testid="mark-all-read-btn">
              <CheckCheck size={14} className="mr-1.5" />Mark all read
            </Button>
          )}
        </div>

        {/* Health alert mute banner — visible to super-admins only (backend filters) */}
        {data.health_alert_mute && (
          <Card className="border-warning/30 bg-warning/5" data-testid="health-mute-banner">
            <CardContent className="p-3 flex items-center gap-3 flex-wrap">
              <BellOff size={16} className="text-warning shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold text-foreground">
                  Health alerts muted until {new Date(data.health_alert_mute.mute_until).toLocaleString()}
                </p>
                {data.health_alert_mute.reason && (
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    Reason: {data.health_alert_mute.reason} · by {data.health_alert_mute.muted_by}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    await api.delete('/admin/mute-health-alerts');
                    toast.success('Health alerts resumed');
                    fetchData();
                  } catch (e) { toast.error('Failed to unmute'); }
                }}
                data-testid="notif-unmute-btn"
              >
                <Bell size={14} className="mr-1" /> Unmute now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Recent Health Incidents — last 3 unhealthy states with timestamp + failing sections */}
        {data.recent_health_incidents && data.recent_health_incidents.length > 0 && (
          <Card className="border-error/20 bg-error/5" data-testid="recent-health-incidents-banner">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Activity size={15} className="text-error" />
                  <p className="text-[13px] font-semibold text-foreground">Recent ERP Health Incidents</p>
                  <Badge variant="outline" className="text-[10px] h-5">{data.recent_health_incidents.length}</Badge>
                </div>
                <button
                  onClick={() => navigate('/erp-health-report')}
                  className="text-[11px] font-medium text-error hover:underline"
                  data-testid="view-health-report-link"
                >
                  Open report →
                </button>
              </div>
              <div className="space-y-2">
                {data.recent_health_incidents.map((inc, i) => {
                  const sev = (inc.overall || '').toLowerCase();
                  const sevColor = sev === 'error' ? 'text-error bg-error/10 border-error/30'
                    : sev === 'warning' ? 'text-warning bg-warning/10 border-warning/30'
                    : 'text-muted-foreground bg-muted/50 border-border';
                  const failing = Array.isArray(inc.failing) ? inc.failing : [];
                  return (
                    <div
                      key={i}
                      className="flex items-start gap-3 p-2.5 rounded-lg bg-background/60 border border-border/60"
                      data-testid={`health-incident-${i}`}
                    >
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${sevColor} shrink-0 mt-0.5`}>
                        {sev || 'incident'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-muted-foreground font-medium">{formatTime(inc.fired_at)}</p>
                        <p className="text-[12px] text-foreground mt-0.5 truncate">
                          {failing.length > 0 ? failing.slice(0, 3).join(' · ') : 'Multiple sections reported issues'}
                          {failing.length > 3 && <span className="text-muted-foreground"> +{failing.length - 3} more</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Free Tier Banner */}
        {tierData && !tierData.is_free_tier && (
          <Card className="border-0 bg-gradient-to-r from-amber-50 to-orange-50 shadow-sm" data-testid="tier-upgrade-banner">
            <CardContent className="py-4 px-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-warning/20 flex items-center justify-center shrink-0">
                  <AlertTriangle size={20} className="text-warning" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-[14px] text-warning">Upgrade Your Plan</p>
                  <p className="text-[12px] text-warning mt-0.5">
                    {tierData.exceeds_employees && `You have ${tierData.employee_count} employees (free limit: ${tierData.employee_limit}). `}
                    {tierData.exceeds_revenue && `Estimated annual revenue: $${tierData.estimated_annual_revenue_usd.toLocaleString()} (free limit: $50,000). `}
                    Contact support for premium features.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {tierData && tierData.is_free_tier && (
          <Card className="border-0 bg-gradient-to-r from-emerald-50 to-teal-50 shadow-sm" data-testid="tier-free-banner">
            <CardContent className="py-3 px-5">
              <div className="flex items-center gap-2 text-[13px]">
                <CheckCircle2 size={16} className="text-success" />
                <span className="text-success font-medium">Free Plan Active</span>
                <span className="text-success">- {tierData.employee_count}/{tierData.employee_limit} employees</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Total</p>
              <p className="text-2xl font-bold font-outfit text-foreground mt-1">{data.total}</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-error uppercase tracking-[0.08em]">Unread</p>
              <p className="text-2xl font-bold font-outfit text-error mt-1">{data.unread_count}</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-success uppercase tracking-[0.08em]">HR Alerts</p>
              <p className="text-2xl font-bold font-outfit text-success mt-1">{data.notifications.filter(n => n.category === 'hr').length}</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-warning uppercase tracking-[0.08em]">Stock Alerts</p>
              <p className="text-2xl font-bold font-outfit text-warning mt-1">{data.notifications.filter(n => n.category === 'inventory').length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Notifications List */}
        <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl overflow-hidden">
          <CardHeader className="pt-5 px-6 pb-0">
            <CardTitle className="text-[15px] font-outfit font-semibold text-foreground">All Notifications</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 px-4">
            {loading ? (
              <div className="py-12 text-center text-muted-foreground text-[13px]">Loading...</div>
            ) : data.notifications.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <Bell size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-[13px]">No notifications yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {data.notifications.map((n) => {
                  const cfg = CATEGORY_CONFIG[n.category] || CATEGORY_CONFIG.system;
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={n.id}
                      onClick={() => handleClick(n)}
                      className={`w-full text-left p-4 rounded-xl transition-colors ${PRIORITY_STYLES[n.priority] || PRIORITY_STYLES.low} ${
                        n.read ? 'bg-white hover:bg-muted/50' : 'bg-muted/50 hover:bg-muted/50'
                      }`}
                      data-testid="notification-item"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                          <Icon size={15} className={cfg.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-[13px] font-semibold ${n.read ? 'text-muted-foreground' : 'text-foreground'}`}>{n.title}</p>
                            {!n.read && <div className="w-2 h-2 rounded-full bg-error shrink-0" />}
                          </div>
                          <p className="text-[12px] text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                          <p className="text-[10px] text-muted-foreground mt-1.5 font-medium">{formatTime(n.created_at)}</p>
                        </div>
                        {n.action_url && <ChevronRight size={14} className="text-muted-foreground shrink-0 mt-2" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
